import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Award, CheckCircle, XCircle, AlertCircle,
  Mail, Shield, Copy, BadgeCheck, Globe,
  Users, Target, Clock, X, ZoomIn,
  Calendar, Check, Download, FileImage, FileText
} from 'lucide-react';
import { verifyCertificate, PublicCertificateVerificationResponse } from '@/services/publicCertificate.service';
import { useAuth } from '@/contexts/AuthContext';

// Tier config for badge colors
const TIER_CONFIG: Record<string, { label: string; bg: string; text: string; border: string }> = {
  GOLD: { label: 'Gold', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-300' },
  SILVER: { label: 'Silver', bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-300' },
  BRONZE: { label: 'Bronze', bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-300' },
};

// Skeleton loading component
function CertificateVerifySkeleton() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      {/* Header skeleton */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="h-8 w-28 bg-gray-200 rounded-lg animate-pulse" />
            <div className="h-9 w-20 bg-gray-200 rounded-lg animate-pulse" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Status banner skeleton */}
        <div className="mb-6 h-16 bg-gray-100 rounded-xl animate-pulse" />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Certificate preview skeleton */}
          <div className="lg:col-span-2 order-2 lg:order-1">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              <div className="p-4 sm:p-6">
                <div className="aspect-[1.414/1] bg-gray-100 rounded-lg animate-pulse" />
              </div>
              <div className="px-6 sm:px-8 py-6 bg-gray-50 border-t border-gray-200">
                <div className="h-20 bg-gray-100 rounded-xl animate-pulse" />
              </div>
            </div>
          </div>

          {/* Sidebar skeleton */}
          <div className="space-y-6 order-1 lg:order-2">
            {/* Recipient skeleton */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              <div className="h-4 w-32 bg-gray-200 rounded animate-pulse mb-4" />
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gray-200 rounded-full animate-pulse" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse" />
                  <div className="h-3 w-1/2 bg-gray-200 rounded animate-pulse" />
                </div>
              </div>
            </div>

            {/* About skeleton */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              <div className="h-4 w-40 bg-gray-200 rounded animate-pulse mb-4" />
              <div className="h-40 bg-gray-100 rounded-lg animate-pulse mb-4" />
              <div className="h-4 w-2/3 bg-gray-200 rounded animate-pulse mb-2" />
              <div className="h-3 w-full bg-gray-200 rounded animate-pulse" />
            </div>

            {/* Verification details skeleton */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              <div className="h-4 w-36 bg-gray-200 rounded animate-pulse mb-4" />
              <div className="space-y-4">
                <div className="h-10 bg-gray-100 rounded animate-pulse" />
                <div className="h-10 bg-gray-100 rounded animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default function CertificateVerify() {
  const { certificateNumber } = useParams<{ certificateNumber: string }>();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [certificate, setCertificate] = useState<PublicCertificateVerificationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [courseImgError, setCourseImgError] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [descriptionClamped, setDescriptionClamped] = useState(false);
  const descriptionRef = useRef<HTMLParagraphElement>(null);

  // Show the toggle only when the 3-line clamp actually hides text
  useEffect(() => {
    const measure = () => {
      const el = descriptionRef.current;
      if (el && !descriptionExpanded) setDescriptionClamped(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [certificate, descriptionExpanded]);

  useEffect(() => {
    const fetchCertificate = async () => {
      if (!certificateNumber) {
        setError('Certificate number is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await verifyCertificate(certificateNumber);
        setCertificate(data);
      } catch (err: any) {
        setError(err.message || 'Failed to verify certificate');
      } finally {
        setLoading(false);
      }
    };

    fetchCertificate();
  }, [certificateNumber]);

  // Close lightbox on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxOpen(false);
    };
    if (lightboxOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [lightboxOpen]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const getStatusConfig = (status: string, isValid: boolean) => {
    if (!isValid || status === 'REVOKED') {
      return {
        icon: XCircle,
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        label: status === 'REVOKED' ? 'Revoked' : 'Invalid',
      };
    }
    if (status === 'EXPIRED') {
      return {
        icon: AlertCircle,
        color: 'text-amber-600',
        bgColor: 'bg-amber-50',
        borderColor: 'border-amber-200',
        label: 'Expired',
      };
    }
    return {
      icon: CheckCircle,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      label: 'Valid',
    };
  };

  // Skeleton loading state
  if (loading) {
    return <CertificateVerifySkeleton />;
  }

  // Error State
  if (error || !certificate) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-2">
                <img src="/logo/Skillzen Final Logo-01.png" alt="Logo" className="h-8 w-auto object-contain" />
              </Link>
            </div>
          </div>
        </header>

        <div className="flex items-center justify-center py-20">
          <div className="max-w-md w-full mx-4">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 text-center">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-3">Certificate Not Found</h1>
              <p className="text-gray-600 mb-6">
                {error || 'The certificate you are looking for could not be found. Please check the certificate number and try again.'}
              </p>
              <div className="space-y-3">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Certificate Number</p>
                  <p className="font-mono text-gray-900 text-sm break-all">{certificateNumber}</p>
                </div>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 w-full px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                >
                  Go to Homepage
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(certificate.status, certificate.is_valid);
  const StatusIcon = statusConfig.icon;
  const verificationUrl = window.location.href;
  const certificationName = certificate.certification.certification_name || certificate.certification.item_name || 'Certificate';
  const tierConfig = certificate.tier ? TIER_CONFIG[certificate.tier] : null;
  const previewSrc = certificate.preview_image_url || null;
  const companyName = certificate.company_name?.trim() || 'Skillzen';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      {/* Lightbox overlay */}
      {lightboxOpen && previewSrc && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 animate-[fadeIn_0.2s_ease-out]"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={previewSrc}
            alt={`Certificate - ${certificationName}`}
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl animate-[scaleIn_0.2s_ease-out]"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              {certificate.logo_url ? (
                <img src={certificate.logo_url} alt={companyName} className="h-8 w-auto object-contain" />
              ) : (
                <img src="/logo/Skillzen Final Logo-01.png" alt={companyName} className="h-8 w-auto object-contain" />
              )}
            </Link>
            <div className="flex items-center gap-3">
              {authLoading ? (
                <div className="h-9 w-28 bg-gray-100 rounded-lg animate-pulse" />
              ) : isAuthenticated ? (
                <Link
                  to="/"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                >
                  Go to Platform
                </Link>
              ) : (
                <Link
                  to="/login"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                >
                  Sign In
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Status Banner — animated slide-in */}
        <div
          className={`mb-6 p-4 rounded-xl border-2 ${statusConfig.bgColor} ${statusConfig.borderColor} flex items-center gap-3 animate-[slideDown_0.4s_ease-out]`}
        >
          <StatusIcon className={`w-6 h-6 flex-shrink-0 ${statusConfig.color}`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className={`font-semibold ${statusConfig.color}`}>
                Certificate Status: {statusConfig.label}
              </p>
              {/* Tier badge */}
              {tierConfig && (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${tierConfig.bg} ${tierConfig.text} ${tierConfig.border}`}>
                  {tierConfig.label}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5 mt-1 text-sm text-gray-600">
              <Calendar className="w-3.5 h-3.5" />
              <span>Issued on <span className="font-medium text-gray-800">{formatDate(certificate.issued_date)}</span></span>
            </div>
          </div>
          {certificate.is_valid && (
            <div className="flex items-center gap-2 text-emerald-600 flex-shrink-0">
              <BadgeCheck className="w-5 h-5" />
              <span className="text-sm font-medium hidden sm:inline">Verified</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Sidebar — shows first on mobile (order-1), second on desktop (lg:order-2) */}
          <div className="space-y-6 order-1 lg:order-2">
            {/* Certificate Recipient */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 animate-[fadeIn_0.5s_ease-out]">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                Certificate Recipient
              </h3>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                  {certificate.holder.holder_name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">
                    {certificate.holder.holder_name}
                  </p>
                  <p className="text-sm text-gray-500 truncate flex items-center gap-1">
                    <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                    {certificate.holder.masked_email}
                  </p>
                </div>
              </div>
            </div>

            {/* About the Certification */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 animate-[fadeIn_0.6s_ease-out]">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                About the Certification
              </h3>

              {(certificate.certification.banner_image_url || certificate.certification.thumbnail_image_url) && !courseImgError && (
                <div className="mb-4 rounded-lg overflow-hidden border border-gray-200">
                  <img
                    src={certificate.certification.banner_image_url || certificate.certification.thumbnail_image_url}
                    alt={certificationName}
                    loading="lazy"
                    className="w-full h-40 object-cover"
                    onError={() => setCourseImgError(true)}
                  />
                </div>
              )}

              <h4 className="font-semibold text-gray-900 mb-2">
                {certificationName}
              </h4>

              {certificate.certification.description && (
                <div className="mb-4">
                  <p
                    ref={descriptionRef}
                    className={`text-sm text-gray-600 ${descriptionExpanded ? '' : 'line-clamp-3'}`}
                  >
                    {certificate.certification.description}
                  </p>
                  {(descriptionClamped || descriptionExpanded) && (
                    <button
                      type="button"
                      onClick={() => setDescriptionExpanded(prev => !prev)}
                      className="mt-1 text-sm font-medium text-primary-600 hover:text-primary-700"
                    >
                      {descriptionExpanded ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </div>
              )}

              <div className="space-y-3 mb-6">
                {certificate.certification.total_certified !== undefined && certificate.certification.total_certified > 0 && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Users className="w-4 h-4 text-primary-500" />
                    <span>{certificate.certification.total_certified.toLocaleString()} certified</span>
                  </div>
                )}
                {certificate.passing_percentage && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Target className="w-4 h-4 text-primary-500" />
                    <span>Passing score: {certificate.passing_percentage}%</span>
                  </div>
                )}
                {certificate.expiry_date && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock className="w-4 h-4 text-primary-500" />
                    <span>Valid until: {formatDate(certificate.expiry_date)}</span>
                  </div>
                )}
              </div>

              {certificate.certification.tags && certificate.certification.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {certificate.certification.tags.slice(0, 4).map((tag, index) => (
                    <span
                      key={index}
                      className="px-2.5 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Verification Details */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 animate-[fadeIn_0.7s_ease-out]">
              <div className="flex items-center gap-2 mb-5">
                <Shield className="w-4 h-4 text-emerald-600" />
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                  Verification Details
                </h3>
              </div>

              <div className="space-y-3">
                {/* Certificate Number */}
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1.5 block">Certificate Number</label>
                  <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
                    <p className="font-mono text-sm text-gray-900 flex-1 break-all leading-relaxed">
                      {certificate.certificate_number}
                    </p>
                    <button
                      onClick={() => copyToClipboard(certificate.certificate_number, 'cert_number')}
                      className={`p-1.5 rounded-md transition-colors flex-shrink-0 ${
                        copiedField === 'cert_number'
                          ? 'text-emerald-600 bg-emerald-50'
                          : 'text-gray-400 hover:text-primary-600 hover:bg-primary-50'
                      }`}
                      title="Copy certificate number"
                    >
                      {copiedField === 'cert_number' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  {copiedField === 'cert_number' && (
                    <p className="text-xs text-emerald-600 mt-1 animate-[fadeIn_0.2s_ease-out]">Copied!</p>
                  )}
                </div>

                {/* Verification URL */}
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1.5 block">Verification URL</label>
                  <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
                    <p className="text-sm text-primary-600 flex-1 truncate leading-relaxed">
                      {verificationUrl}
                    </p>
                    <button
                      onClick={() => copyToClipboard(verificationUrl, 'verify_url')}
                      className={`p-1.5 rounded-md transition-colors flex-shrink-0 ${
                        copiedField === 'verify_url'
                          ? 'text-emerald-600 bg-emerald-50'
                          : 'text-gray-400 hover:text-primary-600 hover:bg-primary-50'
                      }`}
                      title="Copy verification URL"
                    >
                      {copiedField === 'verify_url' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  {copiedField === 'verify_url' && (
                    <p className="text-xs text-emerald-600 mt-1 animate-[fadeIn_0.2s_ease-out]">Copied!</p>
                  )}
                </div>
              </div>

              {certificate.is_valid && (
                <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2">
                  <BadgeCheck className="w-4.5 h-4.5 text-emerald-500" />
                  <span className="text-sm font-medium text-emerald-600">{`Verified by ${companyName}`}</span>
                </div>
              )}
            </div>

          </div>

          {/* Certificate Preview — shows second on mobile (order-2), first on desktop (lg:order-1) */}
          <div className="lg:col-span-2 order-2 lg:order-1">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden animate-[fadeIn_0.4s_ease-out]">
              {/* Certificate Preview — prefer image (works on mobile), fall back to PDF */}
              {previewSrc ? (
                <div className="w-full bg-white p-4 sm:p-6">
                  <div
                    className="relative bg-white rounded-lg overflow-hidden shadow-sm border border-gray-100 cursor-pointer group"
                    onClick={() => setLightboxOpen(true)}
                  >
                    <img
                      src={previewSrc}
                      alt={`Certificate - ${certificationName}`}
                      className="w-full h-auto border-0 bg-white"
                    />
                    {/* Zoom hint overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-3 shadow-lg">
                        <ZoomIn className="w-5 h-5 text-gray-700" />
                      </div>
                    </div>
                  </div>

                </div>
              ) : certificate.pdf_url ? (
                <div className="w-full bg-white p-4 sm:p-6">
                  <div className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-100">
                    <object
                      data={`${certificate.pdf_url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                      type="application/pdf"
                      className="w-full aspect-[1.414/1] border-0 bg-white"
                    >
                      <div className="w-full aspect-[1.414/1] flex flex-col items-center justify-center bg-gray-50">
                        <Award className="w-16 h-16 text-gray-300 mb-4" />
                        <p className="text-gray-500">Unable to display certificate preview</p>
                      </div>
                    </object>
                  </div>
                </div>
              ) : (
                <div className="p-8 sm:p-12 text-center bg-white">
                  <Award className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Certificate preview not available</p>
                </div>
              )}

              {/* Download */}
              {(previewSrc || certificate.pdf_url) && (
                <div className="px-4 sm:px-8 py-4 border-t border-gray-200">
                  <div className="relative inline-flex w-full">
                    {/* Split button: main action + dropdown toggle */}
                    <div className="flex w-full rounded-xl overflow-hidden shadow-sm border border-gray-200">
                      {/* Main download area - shows format options inline */}
                      <div className="flex-1 min-w-0 flex items-center gap-2 px-3 sm:px-4 py-3 bg-white">
                        <Download className="w-4.5 h-4.5 text-gray-500" />
                        <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">Download as</span>
                      </div>
                      {/* Format buttons */}
                      <div className="flex items-center border-l border-gray-200">
                        {previewSrc && (
                          <a
                            href={previewSrc}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-3 bg-white hover:bg-blue-50 transition-colors border-r border-gray-200"
                          >
                            <FileImage className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-semibold text-blue-600">PNG</span>
                          </a>
                        )}
                        {certificate.pdf_url && (
                          <a
                            href={certificate.pdf_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-3 bg-white hover:bg-red-50 transition-colors"
                          >
                            <FileText className="w-4 h-4 text-red-500" />
                            <span className="text-sm font-semibold text-red-500">PDF</span>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Verification Description */}
              <div className="px-6 sm:px-8 py-6 bg-gray-50 border-t border-gray-200">
                <div className="p-4 bg-primary-50 rounded-xl border border-primary-100">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    This certificate verifies that{' '}
                    <span className="font-semibold text-primary-700">{certificate.holder.holder_name}</span>{' '}
                    successfully completed the{' '}
                    <span className="font-semibold text-primary-700">{certificationName}</span>{' '}
                    on{' '}
                    <span className="font-semibold">{formatDate(certificate.issued_date)}</span>
                    {certificate.percentage_achieved && (
                      <> with a score of <span className="font-semibold">{certificate.percentage_achieved}%</span></>
                    )}
                    {` . The certificate indicates completion as validated by ${companyName}.`}
                    {certificate.expiry_date && (
                      <> This certificate expires on <span className="font-semibold">{formatDate(certificate.expiry_date)}</span>.</>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-8 sm:mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 text-center sm:text-left">
            <div className="flex items-center gap-2">
              <img src={certificate.logo_url || "/logo/Skillzen Final Logo-01.png"} alt={companyName} className="h-6 w-auto object-contain" />
              <span className="text-gray-400">|</span>
              <span className="text-sm text-gray-600">Certificate Verification</span>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-4 text-sm text-gray-600">
              <span>&copy; {new Date().getFullYear()} {companyName}. All rights reserved.</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Keyframe animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
